import { Button } from '@mantine/core';
import { variants, Modal, Select } from '@uag/react-core';
import { useGetIndustrialSectors } from '@uag/welcome-shared/src/api/queries/industrial-sectors/industrial-sectors';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { create, InstanceProps } from 'react-modal-promise';

type ApproveIndustrialSectorModalResult = { industrialSector: string | undefined };

type ApproveExistingCustomerModalProps = {
    userName?: string;
    email?: string;
    companyName?: string;
} & InstanceProps<void>;

const ApproveIndustrialSectorModal = ({
    isOpen,
    onResolve,
    onReject,
}: InstanceProps<ApproveIndustrialSectorModalResult>) => {
    const { t } = useTranslation();
    const [selectedSector, setSelectedSector] = useState<string | undefined>();

    const { data: industrialSectorsResponse, isPending: areIndustrialSectorsLoading } = useGetIndustrialSectors();
    const industrialSectors = industrialSectorsResponse?.data || [];

    const submit = () => onResolve({ industrialSector: selectedSector });
    const reject = () => onReject();

    return (
        <Modal opened={isOpen} size="lg" title={t('assignIndustrialSector')} onAbort={reject} onClose={reject}>
            <div className="text-text-ultra-light text-base">{t('ApproveModalMessage')}</div>

            <Select
                data={industrialSectors.map(({ identifier, translation }) => ({
                    value: identifier,
                    label: translation || identifier,
                }))}
                disabled={areIndustrialSectorsLoading || industrialSectors.length <= 0}
                maxDropdownHeight={200}
                placeholder={t('selectPlaceholder', { label: t('industrialSector') })}
                value={selectedSector}
                required
                onChange={(value) => setSelectedSector(value ?? undefined)}
            />

            <Modal.Footer>
                <Button className="modal-action" variant={variants.button.secondary} onClick={reject}>
                    {t('cancel')}
                </Button>
                <Button className="modal-action" disabled={!selectedSector} onClick={submit}>
                    {t('assignSector')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const openApproveIndustrialSectorModal = create(ApproveIndustrialSectorModal);

const ApproveExistingCustomerModal = ({
    userName,
    email,
    companyName,
    isOpen,
    onResolve,
    onReject,
}: ApproveExistingCustomerModalProps) => {
    const { t } = useTranslation();

    const submit = () => onResolve();
    const reject = () => onReject();

    return (
        <Modal opened={isOpen} size="lg" title={t('grantAccessQuestion')} onAbort={reject} onClose={reject}>
            <div className="gap-base flex flex-col">
                <div className="text-text-ultra-light text-base">
                    {t('approveExistingCustomerMessage', { companyName })}
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-text-primary text-sm font-medium">{t('user')}</span>
                    <div className="border-border-default border px-4 py-3">
                        <p className="text-text-primary text-sm font-semibold">{userName}</p>
                        <p className="text-text-ultra-light text-sm">{email}</p>
                    </div>
                </div>
            </div>

            <Modal.Footer>
                <Button className="modal-action" variant={variants.button.secondary} onClick={reject}>
                    {t('cancel')}
                </Button>
                <Button className="modal-action" onClick={submit}>
                    {t('confirm')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const openApproveExistingCustomerModal = create(ApproveExistingCustomerModal);
